import React, {useMemo, useState} from 'react';
import {TextInput} from '@mantine/core';
import {Alert} from 'src/design/atoms/Alert';
import {Card} from 'src/design/atoms/Card';
import {Group} from 'src/design/atoms/Group';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {IconSearch} from '@tabler/icons-react';
import type {SkillSignOffPendingResponse} from 'src/api/ProactApiClient';
import {Button} from 'src/design/atoms/Button';
import {ConfirmDialog} from 'src/design/molecules/ConfirmDialog';
import {Pill} from 'src/design/atoms/Pill';
import {Spinner} from 'src/design/atoms/Spinner';
import {
  useCancelSkillSignOffMutation,
  useGetSkillSignOffInboxQuery,
} from 'src/redux/queries/skillSignOff';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {useAlert} from 'src/shared/hooks/useAlert';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

export interface SignOffTraineeGroup {
  userId: number;
  userName: string;
  employeeId?: string;
  maxDaysWaiting: number;
  items: SkillSignOffPendingResponse[];
}

export const groupSignOffsByTrainee = (
  rows: SkillSignOffPendingResponse[]
): SignOffTraineeGroup[] => {
  const groups = new Map<number, SignOffTraineeGroup>();

  rows.forEach(row => {
    const existing = groups.get(row.userId);
    if (existing) {
      existing.items.push(row);
      existing.maxDaysWaiting = Math.max(existing.maxDaysWaiting, row.daysWaiting);
    } else {
      groups.set(row.userId, {
        userId: row.userId,
        userName: row.userName,
        employeeId: row.employeeId ?? undefined,
        maxDaysWaiting: row.daysWaiting,
        items: [row],
      });
    }
  });

  const result = Array.from(groups.values());
  result.forEach(group =>
    group.items.sort((a, b) => b.daysWaiting - a.daysWaiting)
  );
  result.sort((a, b) => b.maxDaysWaiting - a.maxDaysWaiting);
  return result;
};

const daysWaitingColor = (daysWaiting: number): string | undefined =>
  daysWaiting >= 7 ? 'red' : undefined;

/**
 * Evaluator inbox — pending sign-offs grouped by trainee, sorted by wait
 * time (worst-waiting trainee first, worst-waiting item first within each
 * group). Card-per-trainee per the sub-entity collection convention
 * (variable-length, rich content).
 */
export const SignOffInboxTab: React.FC = () => {
  const navigate = useAppNavigate();
  const {setAlert} = useAlert();
  const [search, setSearch] = useState('');
  const [cancelTarget, setCancelTarget] = useState<
    SkillSignOffPendingResponse | undefined
  >();

  const {data, isLoading, isFetching, isError, error} =
    useGetSkillSignOffInboxQuery({
      start: 0,
      length: 500,
      search: search.trim() || undefined,
      mineOnly: true,
    });
  const [cancelSignOff, {isLoading: isCancelling}] =
    useCancelSkillSignOffMutation();

  const groups = useMemo(() => groupSignOffsByTrainee(data ?? []), [data]);

  const handleCancel = async () => {
    if (!cancelTarget) {
      return;
    }
    try {
      await cancelSignOff(cancelTarget.id).unwrap();
      setAlert(
        `The sign-off request for ${cancelTarget.userName} was cancelled.`,
        'success'
      );
      setCancelTarget(undefined);
    } catch (cancelError) {
      setAlert(parseErrorMessage(cancelError), 'error');
    }
  };

  if (isLoading) {
    return <Spinner loadingText="Loading your queue..." />;
  }

  if (isError) {
    return <Alert color="red">{parseErrorMessage(error)}</Alert>;
  }

  return (
    <Stack gap="md">
      <TextInput
        placeholder="Search trainee, employee ID or skill"
        leftSection={<IconSearch size={16} />}
        value={search}
        onChange={event => setSearch(event.currentTarget.value)}
        maw={360}
      />

      {isFetching && groups.length === 0 ? (
        <Spinner loadingText="Loading your queue..." />
      ) : groups.length === 0 ? (
        <Alert color="gray">
          Nothing waiting on you right now. Requests suggesting you (or a
          group you belong to) as evaluator will show up here.
        </Alert>
      ) : (
        groups.map(group => (
          <Card key={group.userId} withBorder shadow="xs" p="md">
            <Group justify="space-between" mb="sm">
              <Group gap="xs" align="baseline">
                <Text fw={600} size="lg">
                  {group.userName}
                </Text>
                {group.employeeId && (
                  <Text c="dimmed" size="sm">
                    {group.employeeId}
                  </Text>
                )}
              </Group>
              <Pill
                text={`Waiting ${Math.round(group.maxDaysWaiting)}d`}
                color={daysWaitingColor(group.maxDaysWaiting)}
              />
            </Group>
            <Stack gap="xs">
              {group.items.map(item => (
                <Group key={item.id} justify="space-between" wrap="wrap">
                  <Stack gap={0}>
                    <Text size="sm" fw={500}>
                      {item.skillName}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {item.courseName
                        ? `${item.courseName} · `
                        : 'Standalone request · '}
                      Waiting {Math.round(item.daysWaiting)} day
                      {Math.round(item.daysWaiting) === 1 ? '' : 's'}
                    </Text>
                  </Stack>
                  <Group gap="xs">
                    <Button
                      size="xs"
                      variant="subtle"
                      color="red"
                      onClick={() => setCancelTarget(item)}>
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      onClick={() =>
                        navigate(
                          PathUtility.TrainingSignOffEvaluate(
                              String(item.id)
                            )
                        )
                      }>
                      Evaluate
                    </Button>
                  </Group>
                </Group>
              ))}
            </Stack>
          </Card>
        ))
      )}

      <ConfirmDialog
        opened={cancelTarget !== undefined}
        onClose={() => setCancelTarget(undefined)}
        onConfirm={handleCancel}
        title="Cancel sign-off request?"
        body={`The pending request for ${cancelTarget?.userName} on "${cancelTarget?.skillName}" will be removed.`}
        confirmationButtonColor="red"
        buttonTextYes="Cancel request"
        buttonTextNo="Keep it"
        confirmationButtonDisabled={isCancelling}
      />
    </Stack>
  );
};
